/**
 * Tiny decoupled event bus for the container-settings feature.
 *
 * The "Container settings" dialog is opened from the container's "…" ellipsis
 * menu, whose action callbacks (see HiddenActionsComponent / ActionSignature)
 * receive only (data, dispatch, history) and cannot reach React component
 * state. Rather than thread an open-callback through the shared header
 * components, the menu action fires `open(recordId)` on this bus and the
 * in-tree <ContainerSettingsDialog> (which lives on the container screen and so
 * keeps full store/router context — unlike an imperative Modal.confirm) opens
 * itself.
 *
 * The same bus carries a `changed` signal: the settings dialog mutates the
 * `container_dashboard` associations directly, and the read-side
 * <ContainerDashboardTabs> subscribes so the tab strip re-queries immediately
 * on change — no page refresh (the "navigate away and back" behaviour Margot
 * flagged 2026-09-15 on operator-task-container-configurable-dashboards).
 */
type Listener = (recordId: string) => void;

const openListeners = new Set<Listener>();
const changedListeners = new Set<Listener>();

export const containerSettingsBus = {
    /** Ask the in-tree dialog for this container to open. */
    open(recordId: string) {
        openListeners.forEach((cb) => cb(recordId));
    },
    /** Subscribe to open requests; returns an unsubscribe function. */
    onOpen(cb: Listener) {
        openListeners.add(cb);
        return () => {
            openListeners.delete(cb);
        };
    },
    /** Signal that this container's dashboard associations changed. */
    notifyChanged(recordId: string) {
        changedListeners.forEach((cb) => cb(recordId));
    },
    /** Subscribe to change signals; returns an unsubscribe function. */
    onChanged(cb: Listener) {
        changedListeners.add(cb);
        return () => {
            changedListeners.delete(cb);
        };
    },
};
